import React, { useMemo } from 'react';
import { useFragment, graphql } from 'react-relay';
import { Stack, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import type { IssueViewRelayFragment } from '@atlassian/jira-issue-fetch-services-common';

import {
	SectionHeading,
	SectionHeadingTitle,
} from '@atlassian/jira-issue-view-common/src/component/section-heading/section-heading-view';

import type {
	ui_issueViewJpdIdeas_JPDIdeasPanel$key,
	ui_issueViewJpdIdeas_JPDIdeasPanel$data,
} from '@atlassian/jira-relay/src/__generated__/ui_issueViewJpdIdeas_JPDIdeasPanel.graphql';
import { IdeaCard } from './idea-card';
import { messages } from './messages';

const PRODUCT_DISCOVERY = 'PRODUCT_DISCOVERY';

export const JPDIdeasPanel = ({
	issueViewRelayFragment,
}: {
	issueViewRelayFragment: IssueViewRelayFragment;
}) => {
	const { formatMessage } = useIntl();

	const data: ui_issueViewJpdIdeas_JPDIdeasPanel$data =
		useFragment<ui_issueViewJpdIdeas_JPDIdeasPanel$key>(
			graphql`
				fragment ui_issueViewJpdIdeas_JPDIdeasPanel on JiraIssueLinkConnection {
					edges {
						node {
							... on JiraIssueLink {
								issue {
									key
									fieldsById(
										ids: ["assignee", "issuetype", "priority", "status", "summary", "project"]
									) {
										edges {
											node {
												__typename
												... on JiraSingleLineTextField {
													fieldId
													name
													text
												}
												... on JiraProjectField {
													fieldId
													project {
														projectType
													}
												}
												... on JiraIssueTypeField {
													fieldId
													name
													issueType {
														name
														avatar {
															medium
														}
													}
												}
											}
										}
									}
								}
							}
						}
					}
				}
			`,
			// @ts-expect-error - No overload matches this call.
			issueViewRelayFragment?.issueLinks,
		);

	const ideaLinks = useMemo(
		() =>
			(data?.edges || []).filter((link) =>
				link?.node?.issue?.fieldsById?.edges?.some(
					(edge) =>
						edge?.node?.__typename === 'JiraProjectField' &&
						edge?.node?.project?.projectType === PRODUCT_DISCOVERY,
				),
			),
		[data?.edges],
	);

	return ideaLinks.length ? (
		<Stack space="space.100">
			<SectionHeading>
				<SectionHeadingTitle>{formatMessage(messages.ideasTitle)}</SectionHeadingTitle>
			</SectionHeading>

			<Stack xcss={containerStyles}>
				{ideaLinks.map((link) => {
					const issue = link?.node?.issue;
					if (!issue?.key) {
						return null;
					}
					const summaryEdge = issue?.fieldsById?.edges?.find(
						(edge) =>
							edge?.node?.__typename === 'JiraSingleLineTextField' &&
							edge?.node?.fieldId === 'summary',
					);
					const issueTypeEdge = issue?.fieldsById?.edges?.find(
						(edge) => edge?.node?.__typename === 'JiraIssueTypeField',
					);
					const summary =
						(summaryEdge?.node && 'text' in summaryEdge.node && summaryEdge.node.text) || '';
					const issueType =
						issueTypeEdge?.node && 'issueType' in issueTypeEdge.node
							? issueTypeEdge.node.issueType
							: undefined;

					return (
						<IdeaCard
							key={issue.key}
							issueKey={issue.key}
							summary={summary}
							issueTypeIconUrl={issueType?.avatar?.medium ?? undefined}
							issueTypeName={issueType?.name}
						/>
					);
				})}
			</Stack>
		</Stack>
	) : null;
};

const containerStyles = xcss({
	boxShadow: 'elevation.shadow.raised',
	borderRadius: '3px',
});
